/* globals gtag:false */
import { i18n, waitForTranslations } from "./i18n";
import {
  initTrustpilot,
  hideContainers as hideTrustPilotContainers,
} from "./trustpilot.js";
import { initBing, disableBingTracking } from "./bing.js";
import { initHotjar } from "./hotjar.js";
import { enableFacebookPixel } from "./analytics/facebook-pixel.js";
import { initTradeDoublerTrackingLibrary } from "./analytics/tradedoubler.js";
import {
  getUserConsent,
  defaultConsents,
  Consent,
  setUserConsent,
  selectAll,
} from "@fruugo/utilities";
import { initPaymentIcons } from "./payment-icons";
import { openModal } from "./components/modal/Modal";
import { ModalType } from "./components/modal/types";
import { ConsentModalContent } from "./components/modal/ConsentModalContent";
import { initRTBHouseTracking } from "./analytics/rtb-house";
import { enablePinterestTracking } from "./analytics/pinterest";
import {
  disableTikTokTracking,
  enableTikTokTracking,
} from "./analytics/tiktok";

export const consentModalBlacklist = ["/help/detail/cookies"];

function consentOverridePresent() {
  const params = new URL(document.location.href).searchParams;
  return params.get("consent") !== null;
}

export function initCookieConsent() {
  selectAll(".js-manage-cookies").forEach((link) =>
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const consents = getUserConsent() || defaultConsents;
      showConsentModal(consents);
    })
  );

  if (consentModalBlacklist.includes(window.location.pathname)) {
    return;
  }

  // load persisted consent
  const consents = getUserConsent();
  if (consents) {
    enableFunctionality(consents);
  } else {
    if (consentOverridePresent()) {
      return;
    }
    showConsentModal(defaultConsents);
  }

  return;
}

function showConsentModal(consents: Consent) {
  waitForTranslations().then(() => {
    openModal(
      ModalType.CookieConsent,
      <ConsentModalContent consents={consents} />,
      {
        allowClose: false,
        ariaLabel: i18n("modal.consent.label"),
        position: "bottom-left",
      }
    );
  });
}

export function updateConsents(consents: Consent) {
  setUserConsent(consents);

  // Send consent data to backend using fetch
  fetch(`/marketplace/api/consent${window.location.search}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(consents),
  }).catch(() => {
    // Don't log errors to preserve Rollbar quota
  });

  enableFunctionality(consents);
}

function reloadAbTests() {
  // ABTEST CXO2-218_Oney
  initPaymentIcons(".js-payment-icons", true);
}

// everything in here needs to be idempotent as this can be called multiple times by the user
function enableFunctionality(consents: Consent) {
  gtag("consent", "update", {
    ad_storage: consents.advertising === true ? "granted" : "denied",
    ad_user_data: consents.advertising === true ? "granted" : "denied",
    ad_personalization: consents.advertising === true ? "granted" : "denied",
    analytics_storage: consents.analytics === true ? "granted" : "denied",
  });

  if (consents.analytics) {
    if (window.hotjarSiteId) {
      initHotjar();
    }

    reloadAbTests();
  }

  if (consents.advertising) {
    initTrustpilot();
    initBing(consents);
    enableFacebookPixel();
    initRTBHouseTracking();
    enablePinterestTracking();
    enableTikTokTracking();
  } else {
    hideTrustPilotContainers();
    disableBingTracking(consents);
    disableTikTokTracking();
  }

  initTradeDoublerTrackingLibrary(consents);

  const consentUpdateEvent = new CustomEvent("consentUpdate", {
    detail: {
      consents,
    },
    bubbles: true,
    cancelable: true,
  });

  document.dispatchEvent(consentUpdateEvent);
}
